import React, { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { getYear, getMonth, getDate, isValid } from 'date-fns';
import classNames from 'classcat';
import * as constants from '@lindar-joy/plugin-default-event-tracking-advanced-browser/lib/cjs/constants';
import amplitude from 'lib/analytics';
import { AccordionVertical } from 'components/Transitions';
import withLazyStyle from 'components/LazyStyle';
import FieldError from 'components/FieldError';
import fieldsetStyle from 'css/components/fieldset.css?lazy';
import fieldStyle from 'css/components/field.css?lazy';
import datepickerStyle from './datepicker.css?lazy';

const DEFAULT_DATE_OPTION = 'select';

const lowerCaseFirst = (v) => v[0].toLowerCase();
const fromElement = (elem) => ({ target: elem });

const selectOption = (textContent) => (
  <option value="select" key="select" disabled>
    {textContent}
  </option>
);

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

/**
 * Returns the error message, if there is one
 * @param {Object} props - component props which includes props from Reform
 * @param {Object} state - component state which includes the various date parts d, m, y
 */
const getErrorMessage = (
  { format, editable, valid, errorMessage },
  { invalid, isOutOfBounds, d, m, y }
) => {
  const periodParts = { d, m, y };
  const rawDateString = format
    .split('/')
    .map(lowerCaseFirst)
    .map((period) => (period === 'm' ? (parseInt(m, 10) + 1).toString() : periodParts[period]))
    .join('/');
  const dateError = invalid && isOutOfBounds ? `Invalid date ${rawDateString}` : 'Invalid date';
  const hasError = editable && (valid === false || invalid === true);
  if (!hasError) return;
  return invalid ? dateError : errorMessage || dateError;
};

/**
 * Extract properties and prepare them for the "Date Input Changed" event.
 * @param {RefObject<HTMLElement>} elemRef - the ref to the element which in this case is the date picker element
 * @param {string} date - the date formated to ISO 8601 e.g. Fri Jan 01 1999 00:00:00
 * @param {Object} props - component props which includes props from Reform
 */
const extractProperties = (elemRef, date, { name, labelText }) => {
  const form = elemRef.current?.querySelector('select')?.form;
  const formName = form?.name;
  return {
    [constants.AMPLITUDE_EVENT_PROP_ELEMENT_NAME]: name,
    [constants.AMPLITUDE_EVENT_PROP_ELEMENT_ID]: elemRef.current?.id,
    [constants.AMPLITUDE_EVENT_PROP_ELEMENT_CLASS]: elemRef.current?.className,
    [constants.AMPLITUDE_EVENT_PROP_ELEMENT_LABEL]: labelText,
    [constants.AMPLITUDE_EVENT_PROP_ELEMENT_VALUE]: date,
    [constants.AMPLITUDE_EVENT_PROP_FORM_NAME]: formName,
    [constants.AMPLITUDE_EVENT_PROP_FORM_ID]: form?.id
  };
};

/** Only comes in Fieldset form due to formatting rules */
class DatePicker extends PureComponent {
  constructor(props) {
    super(props);
    const initialDate = new Date(props.initial);
    this.state = props.initial
      ? {
          y: getYear(initialDate),
          m: getMonth(initialDate),
          d: getDate(initialDate)
        }
      : {
          y: DEFAULT_DATE_OPTION,
          m: DEFAULT_DATE_OPTION,
          d: DEFAULT_DATE_OPTION
        };
    this.state._invalid = null;
    this.state._isOutOfBounds = null;
    this.elemRef = createRef();
  }

  handleChange = (e) => {
    this.setState({ [e.currentTarget.name]: e.target.value }, () => {
      const { onChange, name } = this.props;
      const { y, m, d } = this.state;
      if ([y, m, d].includes(DEFAULT_DATE_OPTION)) return;
      const date = new Date(Date.UTC(y, m, d));
      const isOutOfBounds = date.getMonth() !== parseInt(m, 10);
      const invalid = !(isValid(date) && !isOutOfBounds);
      this.setState({
        _invalid: invalid,
        _isOutOfBounds: isOutOfBounds
      });
      amplitude.track('Date Input Changed', extractProperties(this.elemRef, date, this.props));
      if (onChange && !invalid) {
        onChange(fromElement(this.elemRef.current), { value: date.toJSON(), name: name });
      }
    });
  };

  render() {
    const {
      format: dateFormat,
      minY,
      maxY,
      valid,
      editable,
      required,
      labelText,
      errorMessage,
      hideAsterisk,
      showOptional,
      testId
    } = this.props;
    const { y, m, d, _invalid, _isOutOfBounds } = this.state;

    const selectYear = (
      <select
        className="dropdown datepicker__select datepicker__select--year"
        key="year"
        id="datepicker__select--year"
        name="y"
        value={y}
        onChange={this.handleChange}
        data-test-id={`${testId}-y`}
      >
        {[
          selectOption('Year'),
          ...[...new Array(maxY - minY)]
            .map((val, index) => {
              const year = minY + index + 1;
              return (
                <option value={year} key={year}>
                  {year}
                </option>
              );
            })
            .reverse()
        ]}
      </select>
    );

    const selectMonth = (
      <select
        className="dropdown datepicker__select datepicker__select--month"
        key="month"
        id="datepicker__select--month"
        value={m}
        name="m"
        onChange={this.handleChange}
        data-test-id={`${testId}-m`}
      >
        {[
          selectOption('Month'),
          ...months.map((val, index) => (
            <option value={index} key={val}>
              {val}
            </option>
          ))
        ]}
      </select>
    );

    const selectDay = (
      <select
        className="dropdown datepicker__select datepicker__select--day"
        key="day"
        id="datepicker__select--day"
        value={d}
        name="d"
        onChange={this.handleChange}
        data-test-id={`${testId}-d`}
      >
        {[
          selectOption('Day'),
          ...[...new Array(31)].map((val, index) => (
            /* eslint-disable-next-line react/no-array-index-key -- day always mirrors index */
            <option value={index + 1} key={index + 1}>
              {index + 1}
            </option>
          ))
        ]}
      </select>
    );

    const periodMap = {
      y: [selectYear, 'datepicker__select--year'],
      m: [selectMonth, 'datepicker__select--month'],
      d: [selectDay, 'datepicker__select--day']
    };

    const firstId = periodMap[dateFormat[0].toLowerCase()][1];

    const ordered = dateFormat
      .split('/')
      .map(lowerCaseFirst)
      .map((period) => periodMap[period][0]);

    const notEmpty =
      d !== DEFAULT_DATE_OPTION && m !== DEFAULT_DATE_OPTION && y !== DEFAULT_DATE_OPTION;
    const fieldClass = classNames([
      'fieldset',
      'fieldset--dynamic',
      {
        notEmpty: true,
        valid: notEmpty && valid,
        invalid: editable && (valid === false || _invalid === true)
      }
    ]);
    const _errorMessage = getErrorMessage(
      { format: dateFormat, editable, valid, errorMessage },
      { invalid: _invalid, isOutOfBounds: _isOutOfBounds, d, m, y }
    );

    return (
      <div className={fieldClass}>
        <label className="fieldset__label datepicker__label" htmlFor={firstId}>
          {`${required && !hideAsterisk ? '* ' : ''}${labelText}${
            !required && showOptional ? ' (optional)' : ''
          }`}
        </label>
        <div className="datepicker" ref={this.elemRef}>
          {ordered}
        </div>
        <AccordionVertical childKey="datepicker-errorMsg" className="errorMsg-container">
          {_errorMessage ? (
            <FieldError
              errorMessage={_errorMessage}
              fieldDomId={firstId}
              // ISO 8601 (without timezone) date format Amplitude prefers https://amplitude.com/docs/apis/analytics/http-v2#set-date-values
              sanitizedFieldValue={new Date(Date.UTC(y, m, d)).toJSON()?.slice(0, -5)}
              fieldRef={this.elemRef}
            />
          ) : null}
        </AccordionVertical>
      </div>
    );
  }
}

DatePicker.propTypes = {
  /** The default date to be selected in the dropdowns */
  initial: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  /** `/` delimited string with 'days', 'months', 'years'. For example: 'YYYY/MM/DD' */
  format: PropTypes.string,
  name: PropTypes.string,
  labelText: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  editable: PropTypes.bool,
  required: PropTypes.bool,
  valid: PropTypes.bool,
  hideAsterisk: PropTypes.bool,
  showOptional: PropTypes.bool,
  onChange: PropTypes.func,
  minY: PropTypes.number,
  maxY: PropTypes.number,
  testId: PropTypes.string
};

DatePicker.defaultProps = {
  initial: null,
  editable: true,
  valid: null,
  required: false,
  hideAsterisk: false,
  showOptional: false,
  onChange: null,
  errorMessage: '',
  name: 'datepicker',
  format: 'd/m/y',
  minY: 1912,
  maxY: getYear(Date.now()),
  testId: null
};

export default withLazyStyle([datepickerStyle, fieldsetStyle, fieldStyle])(DatePicker);
export { default as DatePickerDropdown } from './DatePickerDropdown';
